import { useState, useEffect, useRef } from "react";
import { Plus, MapPin, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { locationsApi } from "@/lib/api";

/**
 * Multi-location picker with inline editable detail cards.
 * - Each selected location renders as a card showing address + inline editable
 *   `parking_info` field (auto-saves on blur, write-through to the Location record).
 * - `onLocationUpdated` (optional) is called after a successful save so the parent
 *   page can re-fetch the canonical `locations` list.
 */
export default function LocationsMultiSelect({
  value,
  onChange,
  locations,
  onAddNew,
  onLocationUpdated,
  testIdPrefix = "loc-select",
}) {
  const [open, setOpen] = useState(false);
  const selected = value || [];
  const byId = Object.fromEntries(locations.map((l) => [l.id, l]));

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  const remove = (id) => onChange(selected.filter((x) => x !== id));

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="space-y-2">
          {selected.map((id) => {
            const loc = byId[id];
            if (!loc) return null;
            return (
              <SelectedLocationCard
                key={id}
                loc={loc}
                onRemove={() => remove(id)}
                onLocationUpdated={onLocationUpdated}
                testIdPrefix={testIdPrefix}
              />
            );
          })}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testIdPrefix}-open`}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-zinc-950 border border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm transition-colors w-full justify-between"
          >
            <span className="text-zinc-500">
              {selected.length === 0
                ? "Select locations…"
                : `${selected.length} selected · add more`}
            </span>
            <Plus className="w-3.5 h-3.5 text-amber-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="bg-zinc-950 border-zinc-800 text-zinc-100 p-1 w-[--radix-popover-trigger-width] max-h-72 overflow-auto"
          align="start"
        >
          <button
            type="button"
            data-testid={`${testIdPrefix}-add-new`}
            onClick={() => {
              setOpen(false);
              onAddNew && onAddNew();
            }}
            className="w-full text-left px-3 py-2 rounded-sm text-sm text-amber-500 hover:bg-zinc-900 inline-flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add new location…
          </button>
          {locations.length > 0 && (
            <div className="my-1 border-t border-zinc-800" />
          )}
          {locations.length === 0 ? (
            <div className="px-3 py-3 text-xs text-zinc-500 italic">
              No locations in your library yet.
            </div>
          ) : (
            locations.map((l) => {
              const isSelected = selected.includes(l.id);
              return (
                <button
                  type="button"
                  key={l.id}
                  data-testid={`${testIdPrefix}-opt-${l.id}`}
                  onClick={() => toggle(l.id)}
                  className={`w-full text-left px-3 py-2 rounded-sm text-sm inline-flex items-center gap-2 hover:bg-zinc-900 ${
                    isSelected ? "text-zinc-100" : "text-zinc-400"
                  }`}
                >
                  <span
                    className={`w-3.5 h-3.5 rounded-sm border ${
                      isSelected
                        ? "bg-amber-500 border-amber-500"
                        : "border-zinc-700"
                    }`}
                  />
                  <span className="flex-1 truncate">
                    <span className="font-medium">{l.name}</span>
                    {l.address && (
                      <span className="text-zinc-500 ml-2 text-xs">
                        — {l.address.split(",")[0]}
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function SelectedLocationCard({
  loc,
  onRemove,
  onLocationUpdated,
  testIdPrefix,
}) {
  const [parking, setParking] = useState(loc.parking_info || "");
  const [saving, setSaving] = useState(false);
  const initialRef = useRef(loc.parking_info || "");

  // Keep local input synced when the upstream prop changes (e.g. background reload)
  useEffect(() => {
    setParking(loc.parking_info || "");
    initialRef.current = loc.parking_info || "";
  }, [loc.parking_info]);

  const persistIfChanged = async () => {
    if (parking === initialRef.current) return;
    setSaving(true);
    try {
      await locationsApi.update(loc.id, { parking_info: parking });
      initialRef.current = parking;
      onLocationUpdated && onLocationUpdated();
    } catch {
      toast.error(`Couldn't save parking notes for ${loc.name}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`${testIdPrefix}-card-${loc.id}`}
      className="rounded-sm border border-zinc-800 border-l-2 border-l-amber-500/60 bg-zinc-950 px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-amber-500/80 shrink-0" />
            <span className="text-sm font-medium text-zinc-100 truncate">
              {loc.name}
            </span>
          </div>
          {loc.address && (
            <div className="text-xs text-zinc-500 mt-0.5 truncate pl-4">
              {loc.address}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          data-testid={`${testIdPrefix}-remove-${loc.id}`}
          className="text-zinc-500 hover:text-rose-400 transition-colors shrink-0 p-0.5"
          title="Remove from project"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-overline text-zinc-500 shrink-0 inline-flex items-center gap-1">
          🅿 <span>Parking</span>
        </span>
        <input
          type="text"
          value={parking}
          onChange={(e) => setParking(e.target.value)}
          onBlur={persistIfChanged}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          data-testid={`${testIdPrefix}-parking-${loc.id}`}
          placeholder="e.g. Free lot on west side, valet at front, street only…"
          className="flex-1 bg-transparent border-0 border-b border-zinc-800 focus:border-amber-500 focus:outline-none text-xs text-zinc-200 placeholder:text-zinc-600 px-0 py-1"
        />
        {saving && <Loader2 className="w-3 h-3 text-amber-500 animate-spin" />}
      </div>
    </div>
  );
}
